import { useEffect, useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Ban,
  CheckCircle2,
  ChevronLeft,
  Coins,
  ChevronRight,
  Download,
  ExternalLink,
  Eye,
  FilePenLine,
  FilterX,
  Hash,
  Loader2,
  Megaphone,
  PlusCircle,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  XCircle,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DatePickerWithRange,
  perkDatePresetRange,
} from "@/components/ui/date-range-picker";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { apiErrorMessage } from "@/api/perkpoint/client";
import {
  PerkPointAuditApi,
  auditExplorerUrl,
  auditRangeParams,
  blockchainExplorerUrl,
  type AuditEntry,
  type AuditEvent,
  type AuditVerification,
} from "@/api/perkpointAudit.api";
import { cn } from "@/lib/utils";
import { usePerkPointScope } from "../programs/program-scope";

const PAGE_SIZE = 20;
const ALL = "all";
const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const EVENT_STYLES: Record<
  AuditEvent,
  { icon: typeof FilePenLine; className: string }
> = {
  created: { icon: PlusCircle, className: "text-green-600" },
  updated: { icon: FilePenLine, className: "text-sky-600" },
  deleted: { icon: Trash2, className: "text-red-600" },
  submitted: { icon: Megaphone, className: "text-amber-600" },
  approved: { icon: CheckCircle2, className: "text-green-600" },
  rejected: { icon: XCircle, className: "text-red-600" },
  status_changed: { icon: Ban, className: "text-slate-600" },
  reversed: { icon: RotateCcw, className: "text-orange-600" },
  exported: { icon: Download, className: "text-slate-600" },
  points: { icon: Coins, className: "text-amber-600" },
  other: { icon: AlertTriangle, className: "text-slate-500" },
};

const AVATAR_COLORS = [
  "bg-sky-100 text-sky-800",
  "bg-violet-100 text-violet-800",
  "bg-amber-100 text-amber-800",
  "bg-rose-100 text-rose-800",
  "bg-teal-100 text-teal-800",
  "bg-indigo-100 text-indigo-800",
];

function initials(name: string) {
  const parts = name.replace(/@.*/, "").split(/[\s._-]+/).filter(Boolean);
  return (parts[0]?.[0] ?? "?").concat(parts[1]?.[0] ?? "").toUpperCase();
}

function avatarClass(entry: AuditEntry) {
  if (entry.user.type !== "admin") return "bg-slate-200 text-slate-700";
  const key = entry.user.id ?? entry.user.name;
  let hash = 0;
  for (const char of key) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function displayValue(value: unknown) {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function EventCell({ entry }: { entry: AuditEntry }) {
  const style = EVENT_STYLES[entry.event] ?? EVENT_STYLES.other;
  const Icon = style.icon;
  return (
    <div className="flex items-center gap-2">
      <Icon className={cn("h-4 w-4 shrink-0", style.className)} />
      <span className="text-sm text-slate-800">{entry.event_label}</span>
    </div>
  );
}

function UserCell({ entry }: { entry: AuditEntry }) {
  return (
    <div className="flex items-center gap-2.5">
      <Avatar className="h-8 w-8">
        <AvatarFallback
          className={cn("text-xs font-semibold", avatarClass(entry))}
        >
          {initials(entry.user.name)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-slate-900">
          {entry.user.name}
        </p>
        {entry.user.email && entry.user.email !== entry.user.name ? (
          <p className="truncate text-xs text-muted-foreground">
            {entry.user.email}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function ObjectCell({ entry }: { entry: AuditEntry }) {
  const showCode = entry.entity.code && entry.entity.code !== entry.object_name;
  return (
    <div className="min-w-0">
      <p className="text-sm font-medium text-slate-800">{entry.object_label}</p>
      <p className="max-w-[200px] truncate text-xs text-muted-foreground">
        {[showCode ? entry.entity.code : null, entry.object_name]
          .filter(Boolean)
          .join(" · ") || entry.entity.id.slice(0, 8)}
      </p>
    </div>
  );
}

function LoadingRows({ columns }: { columns: number }) {
  return (
    <>
      {Array.from({ length: 6 }).map((_, row) => (
        <TableRow key={row}>
          {Array.from({ length: columns }).map((__, cell) => (
            <TableCell key={cell}>
              <Skeleton className="h-4 w-full max-w-[160px]" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function pageNumbers(current: number, total: number): (number | "gap")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const result: (number | "gap")[] = [];
  sorted.forEach((page, index) => {
    if (index > 0 && page - sorted[index - 1] > 1) result.push("gap");
    result.push(page);
  });
  return result;
}

function IntegrityPanel({ entry }: { entry: AuditEntry }) {
  const [result, setResult] = useState<AuditVerification | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");

  const verify = async () => {
    setChecking(true);
    setError("");
    try {
      setResult(await PerkPointAuditApi.verify(entry.program.id));
    } catch (err) {
      setError(apiErrorMessage(err, "Could not check the audit trail"));
    } finally {
      setChecking(false);
    }
  };

  // The chain is intact up to this entry when the break, if any, comes later.
  const intact =
    result &&
    (result.verified || (result.broken_at && result.broken_at.seq > entry.seq));

  return (
    <div
      className={cn(
        "rounded-xl border p-4",
        result
          ? intact
            ? "border-emerald-200 bg-emerald-50/70"
            : "border-red-200 bg-red-50/70"
          : "border-slate-200 bg-slate-50/70",
      )}
    >
      <div className="flex items-center gap-2">
        {result ? (
          intact ? (
            <ShieldCheck className="h-4 w-4 text-emerald-700" />
          ) : (
            <ShieldAlert className="h-4 w-4 text-red-700" />
          )
        ) : (
          <Hash className="h-4 w-4 text-slate-600" />
        )}
        <p
          className={cn(
            "text-sm font-semibold",
            result ? (intact ? "text-emerald-800" : "text-red-800") : "text-slate-800",
          )}
        >
          {result
            ? intact
              ? "Record verified"
              : "Record does not match"
            : "Tamper-evident record"}
        </p>
      </div>
      <p className="mt-1 text-xs text-slate-600">
        {result
          ? intact
            ? `Checked ${result.checked.toLocaleString()} entries for ${entry.program.name}. This entry and everything before it are unchanged.`
            : `Entry #${result.broken_at?.seq} no longer matches its hash (${result.broken_at?.reason.replace(/_/g, " ")}). The history may have been altered.`
          : "Each entry is chained to the one before it by a SHA-256 hash, so any later change to the history shows up."}
      </p>
      <dl className="mt-4 space-y-3 text-sm">
        <div className="flex justify-between gap-4">
          <dt className="text-slate-600">Entry</dt>
          <dd className="font-mono text-slate-900">#{entry.seq}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Hash
          </dt>
          <dd className="mt-1 break-all font-mono text-xs text-slate-900">
            {entry.hash}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Previous hash
          </dt>
          <dd className="mt-1 break-all font-mono text-xs text-slate-600">
            {entry.prev_hash}
          </dd>
        </div>
      </dl>
      {error ? <p className="mt-3 text-xs text-red-700">{error}</p> : null}
      <Button
        type="button"
        variant="outline"
        className="mt-4 bg-white"
        disabled={checking}
        onClick={() => void verify()}
      >
        {checking ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <ShieldCheck className="mr-2 h-4 w-4" />
        )}
        {result ? "Check again" : "Verify audit trail"}
      </Button>
    </div>
  );
}

function AuditDetailsSheet({
  entry,
  open,
  onOpenChange,
}: {
  entry: AuditEntry | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const when = entry ? new Date(entry.created_at) : null;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
        <SheetTitle className="pr-8 text-xl font-bold tracking-tight text-slate-900">
          {entry ? entry.details : "Audit details"}
        </SheetTitle>
        <SheetDescription className="mt-1">
          {entry && when
            ? `${format(when, "MMM d, yyyy 'at' h:mm:ss a")} · ${entry.program.name}`
            : null}
        </SheetDescription>
        {entry ? (
          <div className="mt-6 space-y-6">
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Event</dt>
                <dd>
                  <EventCell entry={entry} />
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Object</dt>
                <dd className="text-right font-medium text-slate-900">
                  {entry.object_label}
                  {entry.object_name ? ` · ${entry.object_name}` : ""}
                  <span className="mt-0.5 block break-all text-xs font-normal text-muted-foreground">
                    {entry.object_id}
                  </span>
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">User</dt>
                <dd className="text-right font-medium text-slate-900">
                  {entry.user.name}
                  {entry.user.email && entry.user.email !== entry.user.name ? (
                    <span className="block text-xs font-normal text-muted-foreground">
                      {entry.user.email}
                    </span>
                  ) : null}
                </dd>
              </div>
              {entry.reason ? (
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Reason</dt>
                  <dd className="text-right text-slate-900">{entry.reason}</dd>
                </div>
              ) : null}
              {entry.ip_address ? (
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">IP address</dt>
                  <dd className="font-mono text-xs text-slate-900">
                    {entry.ip_address}
                  </dd>
                </div>
              ) : null}
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Action</dt>
                <dd className="font-mono text-xs text-slate-700">{entry.action}</dd>
              </div>
            </dl>

            {entry.changes.length ? (
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Changes</h3>
                <div className="mt-2 overflow-hidden rounded-lg border border-slate-200">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                        <TableHead>Field</TableHead>
                        <TableHead>Before</TableHead>
                        <TableHead>After</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entry.changes.map((change) => (
                        <TableRow key={change.field}>
                          <TableCell className="text-sm capitalize text-slate-700">
                            {change.label}
                          </TableCell>
                          <TableCell className="break-all text-sm text-muted-foreground line-through decoration-slate-300">
                            {displayValue(change.from)}
                          </TableCell>
                          <TableCell className="break-all text-sm font-medium text-slate-900">
                            {displayValue(change.to)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : null}

            {entry.before || entry.after ? (
              <details className="group rounded-lg border border-slate-200 p-3 text-sm">
                <summary className="cursor-pointer font-medium text-slate-700">
                  Recorded data
                </summary>
                <div className="mt-3 space-y-3">
                  {entry.before ? (
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Before
                      </p>
                      <pre className="mt-1 overflow-x-auto rounded bg-slate-50 p-2 text-xs text-slate-800">
                        {JSON.stringify(entry.before, null, 2)}
                      </pre>
                    </div>
                  ) : null}
                  {entry.after ? (
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        After
                      </p>
                      <pre className="mt-1 overflow-x-auto rounded bg-slate-50 p-2 text-xs text-slate-800">
                        {JSON.stringify(entry.after, null, 2)}
                      </pre>
                    </div>
                  ) : null}
                </div>
              </details>
            ) : null}

            <IntegrityPanel key={entry.id} entry={entry} />
            {blockchainExplorerUrl ? (
              <Button asChild variant="outline" className="w-full">
                <a
                  href={auditExplorerUrl(entry) ?? blockchainExplorerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View on blockchain explorer
                </a>
              </Button>
            ) : null}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

export default function AuditHistory() {
  const { session } = usePerkPointScope();
  const sessionProgramId =
    session?.programId && UUID.test(session.programId) ? session.programId : null;

  const [programId, setProgramId] = useState<string>(sessionProgramId ?? ALL);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [event, setEvent] = useState(ALL);
  const [objectType, setObjectType] = useState(ALL);
  const [user, setUser] = useState(ALL);
  const [sort, setSort] = useState<"desc" | "asc">("desc");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AuditEntry | null>(null);
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    ...perkDatePresetRange("last30"),
  });

  // A manager session pins the page to its program.
  useEffect(() => {
    if (sessionProgramId) setProgramId(sessionProgramId);
  }, [sessionProgramId]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const programs = useQuery({
    queryKey: ["perkpoint", "programs", "audit-filter"],
    queryFn: () => PerkPointAuditApi.programs(),
    enabled: !sessionProgramId,
    staleTime: 60_000,
  });

  const range = auditRangeParams(dateRange);
  const scopedProgram = programId === ALL ? null : programId;
  const audit = useQuery({
    queryKey: [
      "perkpoint",
      "audit",
      scopedProgram,
      range.from,
      range.to,
      search,
      event,
      objectType,
      user,
      sort,
      page,
    ],
    queryFn: () =>
      PerkPointAuditApi.list({
        programId: scopedProgram,
        ...range,
        search,
        event: event === ALL ? undefined : event,
        object_type: objectType === ALL ? undefined : objectType,
        user_id: user === ALL ? undefined : user,
        sort,
        page,
        page_size: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const items = audit.data?.items ?? [];
  const total = audit.data?.total ?? 0;
  const facets = audit.data?.facets;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);
  const showProgram = !scopedProgram;
  const columns = showProgram ? 7 : 6;
  const filtersActive =
    searchInput !== "" || event !== ALL || objectType !== ALL || user !== ALL;

  const resetPage = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setEvent(ALL);
    setObjectType(ALL);
    setUser(ALL);
    setPage(1);
  };

  const programName = useMemo(() => {
    if (sessionProgramId) return session?.programName;
    return programs.data?.find((program) => program.id === programId)?.name;
  }, [programs.data, programId, session?.programName, sessionProgramId]);

  return (
    <div className="space-y-6 pb-2">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Audit History
          </h1>
          <p className="mt-1 text-muted-foreground">
            Every change to{" "}
            {programName ? (
              <span className="font-medium text-slate-700">{programName}</span>
            ) : (
              "your programs"
            )}
            : program settings, tiers, cost per point, campaigns, and members.
          </p>
        </div>
        <DatePickerWithRange
          presets
          date={dateRange}
          setDate={(value) => {
            setDateRange(value);
            setPage(1);
          }}
          className="w-full sm:w-auto [&_button]:h-9 [&_button]:bg-white [&_button]:text-slate-700"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm xl:flex-row xl:items-center">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by user, object, ID or reason..."
            className="h-10 pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {!sessionProgramId ? (
            <Select value={programId} onValueChange={resetPage(setProgramId)}>
              <SelectTrigger className="h-10 w-[170px] bg-white">
                <SelectValue placeholder="All programs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All programs</SelectItem>
                {(programs.data ?? []).map((program) => (
                  <SelectItem key={program.id} value={program.id}>
                    {program.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          <Select value={event} onValueChange={resetPage(setEvent)}>
            <SelectTrigger className="h-10 w-[150px] bg-white">
              <SelectValue placeholder="All events" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All events</SelectItem>
              {(facets?.events ?? []).map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label} ({option.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={objectType} onValueChange={resetPage(setObjectType)}>
            <SelectTrigger className="h-10 w-[150px] bg-white">
              <SelectValue placeholder="All objects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All objects</SelectItem>
              {(facets?.object_types ?? []).map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label} ({option.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={user} onValueChange={resetPage(setUser)}>
            <SelectTrigger className="h-10 w-[160px] bg-white">
              <SelectValue placeholder="All users" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All users</SelectItem>
              {(facets?.users ?? []).map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {filtersActive ? (
            <Button
              type="button"
              variant="ghost"
              className="h-10 text-slate-600"
              onClick={clearFilters}
            >
              <FilterX className="mr-1.5 h-4 w-4" />
              Clear
            </Button>
          ) : null}
        </div>
      </div>

      {/* Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="space-y-4 p-4 sm:p-5">
          {audit.isError ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-red-200 bg-red-50/60 px-4 py-10 text-center">
              <AlertTriangle className="h-6 w-6 text-red-600" />
              <p className="text-sm text-red-800">
                {apiErrorMessage(audit.error, "Could not load the audit history")}
              </p>
              <Button
                type="button"
                variant="outline"
                className="bg-white"
                onClick={() => void audit.refetch()}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Try again
              </Button>
            </div>
          ) : (
            <div
              className={cn(
                "w-full overflow-x-auto rounded-lg border border-slate-200 transition-opacity",
                audit.isFetching && !audit.isLoading && "opacity-60",
              )}
            >
              <Table className="min-w-[1000px]">
                <TableHeader>
                  <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                    <TableHead className="whitespace-nowrap">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 hover:text-slate-900"
                        onClick={() => {
                          setSort((value) => (value === "desc" ? "asc" : "desc"));
                          setPage(1);
                        }}
                        aria-label={`Sort by time, ${sort === "desc" ? "oldest" : "newest"} first`}
                      >
                        Time
                        {sort === "desc" ? (
                          <ArrowDown className="h-3.5 w-3.5" />
                        ) : (
                          <ArrowUp className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead className="whitespace-nowrap">User</TableHead>
                    <TableHead className="whitespace-nowrap">Event</TableHead>
                    <TableHead className="whitespace-nowrap">Object</TableHead>
                    {showProgram ? (
                      <TableHead className="whitespace-nowrap">Program</TableHead>
                    ) : null}
                    <TableHead className="min-w-[300px]">Details</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {audit.isLoading ? (
                    <LoadingRows columns={columns} />
                  ) : items.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns}
                        className="py-12 text-center text-sm text-muted-foreground"
                      >
                        {filtersActive
                          ? "No activity matches these filters."
                          : "No activity in this date range."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((entry) => {
                      const when = new Date(entry.created_at);
                      return (
                        <TableRow
                          key={entry.id}
                          className="cursor-pointer"
                          onClick={() => setSelected(entry)}
                        >
                          <TableCell className="whitespace-nowrap">
                            <p className="text-sm font-medium text-slate-800">
                              {format(when, "MMM d, yyyy")}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {format(when, "h:mm a")}
                            </p>
                          </TableCell>
                          <TableCell className="max-w-[220px]">
                            <UserCell entry={entry} />
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            <EventCell entry={entry} />
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            <ObjectCell entry={entry} />
                          </TableCell>
                          {showProgram ? (
                            <TableCell className="max-w-[160px] truncate text-sm text-slate-700">
                              {entry.program.name}
                            </TableCell>
                          ) : null}
                          <TableCell className="text-sm text-slate-600">
                            {entry.details}
                            {entry.reason ? (
                              <span className="mt-0.5 block text-xs text-muted-foreground">
                                Reason: {entry.reason}
                              </span>
                            ) : null}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              aria-label="View details"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelected(entry);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()} of{" "}
              {total.toLocaleString()} activities
            </p>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {pageNumbers(page, totalPages).map((item, index) =>
                item === "gap" ? (
                  <span key={`gap-${index}`} className="px-1 text-sm text-muted-foreground">
                    …
                  </span>
                ) : (
                  <Button
                    key={item}
                    type="button"
                    variant={page === item ? "default" : "outline"}
                    size="icon"
                    className={cn(
                      "h-8 min-w-8 px-2",
                      page === item && "bg-green-600 text-white hover:bg-green-700",
                    )}
                    onClick={() => setPage(item)}
                  >
                    {item}
                  </Button>
                ),
              )}
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <AuditDetailsSheet
        entry={selected}
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      />
    </div>
  );
}
